import { Controller } from "@hotwired/stimulus"
import { errorDetail, formatDate, getAllJSON, getJSON, sendJSON } from "../api.js"
import { setComboOptions, comboValue, clearCombo } from "../combobox_helpers.js"

// Helpdesk: lista wątków + widok wątku + formularz nowego wątku (klient) / inbox (staff).
// Jedna strona, widoki przełączane `hidden`. Zero innerHTML — rendering przez <template>
// + textContent. Paginacja server-side (?page=). Lista klienta renderowana tu (rows target); inbox staffu to data-table
// (filtr statusu, szukaj, paginacja w tabeli) — kontroler obsługuje jego akcje wierszy (`rowAction`) i widok wątku.
// „Zamknij wątek" dostępny z widoku wątku (klient i staff); z listy — tylko staff (akcja wiersza).
// Audit target (B-16): present only in staff panel; fetched only when hasAuditTarget.
const AUDIT_LABELS = {
  "helpdesk.opened": "Otwarto",
  "helpdesk.answered": "Odpowiedziano",
  "helpdesk.read": "Przeczytano",
  "helpdesk.closed": "Zamknięto",
  "helpdesk.reopened": "Otwarto ponownie",
}

// client list: thread status as a coloured badge, like the other pages
const STATUS_BADGE = { new: "warm", answered: "sent", closed: "new" }

export default class extends Controller {
  static targets = [
    "listView", "threadView",
    "rows", "status", "pageInfo", "prevBtn", "nextBtn", "pager",
    "reasonSelect", "orderSelect", "subjectInput", "bodyInput", "formError",
    "threadSubject", "threadMeta", "threadCloseBtn", "messages", "replyBody", "replyError",
    "audit", "auditList",
    "replyBtn", "createBtn",
  ]
  static values = {
    url: String,       // /api/helpdesk-threads/
    ordersUrl: String, // /api/orders/ (kontekst zamówienia, opcjonalny)
    staff: Boolean,
  }

  connect() {
    this.page = 1
    this.count = 0
    this.currentThreadId = null
    this.#loadDictionaries()
    // The staff inbox lists threads in a data-table (no rows target); the client page renders its own rows.
    if (this.hasRowsTarget) this.loadList()
  }

  // --- lista wątków ---

  async loadList() {
    let data
    try {
      data = await getJSON(this.#listUrl())
    } catch {
      this.#status("Nie udało się załadować wątków. Odśwież stronę.")
      return
    }
    this.count = data.count ?? 0
    this.rowsTarget.textContent = ""
    if (!data.results?.length) {
      this.#status("Brak wątków.")
    } else {
      this.#status(null)
      const template = document.getElementById("helpdesk-thread-row-template")
      for (const thread of data.results) this.rowsTarget.appendChild(this.#buildRow(template, thread))
    }
    this.#updatePager(data)
  }

  prevPage() {
    if (this.page > 1) {
      this.page -= 1
      this.loadList()
    }
  }

  nextPage() {
    if (this.hasNext) {
      this.page += 1
      this.loadList()
    }
  }

  // --- akcje staffu (inbox) ---

  // Akcja wiersza data-table (`data-table:row-action`, detail {name, item}) → otwórz / przeczytane / zamknij.
  rowAction(event) {
    const { name, item } = event.detail || {}
    if (!item) return undefined
    const wrapped = { params: { id: item.id } }
    if (name === "open") return this.openThread(wrapped)
    if (name === "mark-read") return this.markRead(wrapped)
    if (name === "close") return this.closeThread(wrapped)
    return undefined
  }

  async markRead(event) {
    const { id } = event.params
    const res = await sendJSON("POST", `${this.urlValue}${id}/mark-read/`)
    if (!res.ok) {
      this.#status(errorDetail(res.data, "Nie udało się oznaczyć wątku jako przeczytany."))
      return
    }
    await this.#refreshList()
    await this.#loadAudit()
  }

  async closeThread(event) {
    const { id } = event.params
    const res = await sendJSON("POST", `${this.urlValue}${id}/close/`)
    if (!res.ok) {
      this.#status(errorDetail(res.data, "Nie udało się zamknąć wątku."))
      return
    }
    await this.#refreshList()
    await this.#loadAudit()
  }

  // --- widok wątku ---

  async openThread(event) {
    const { id } = event.params
    this.#resetReply()
    this.currentThreadId = id
    let thread
    try {
      thread = await getJSON(`${this.urlValue}${id}/`)
    } catch {
      this.#status("Nie udało się otworzyć wątku.")
      return
    }
    if (this.hasThreadSubjectTarget) this.threadSubjectTarget.textContent = thread.subject
    if (this.hasThreadMetaTarget) {
      this.threadMetaTarget.textContent = `${thread.status_label} · ${thread.reason_name}`
    }
    if (this.hasThreadCloseBtnTarget) {
      this.threadCloseBtnTarget.hidden = thread.status === "closed"
    }
    await this.#loadMessages()
    await this.#loadAudit()
    this.listViewTarget.hidden = true
    this.threadViewTarget.hidden = false
  }

  backToList() {
    this.#resetReply()
    this.currentThreadId = null
    this.threadViewTarget.hidden = true
    this.listViewTarget.hidden = false
    this.#refreshList()
  }

  async reply() {
    if (this.currentThreadId === null || this.replySending) return
    const body = this.replyBodyTarget.value.trim()
    if (!body) {
      this.#replyError("Wpisz treść odpowiedzi.")
      return
    }
    const generation = this.composerGeneration
    this.replySending = true
    if (this.hasReplyBtnTarget) this.replyBtnTarget.disabled = true
    const res = await sendJSON("POST", `${this.urlValue}${this.currentThreadId}/messages/`, { body })
    if (generation !== this.composerGeneration) return
    this.replySending = false
    if (this.hasReplyBtnTarget) this.replyBtnTarget.disabled = false
    if (!res.ok) {
      this.#replyError(errorDetail(res.data, "Nie udało się wysłać odpowiedzi."))
      return
    }
    this.#replyError(null)
    this.replyBodyTarget.value = ""
    await this.#loadMessages()
    await this.#loadAudit()
  }

  async closeCurrentThread() {
    if (this.currentThreadId === null) return
    const res = await sendJSON("POST", `${this.urlValue}${this.currentThreadId}/close/`)
    if (!res.ok) {
      this.#replyError(errorDetail(res.data, "Nie udało się zamknąć wątku."))
      return
    }
    this.#replyError(null)
    let thread
    try {
      thread = await getJSON(`${this.urlValue}${this.currentThreadId}/`)
    } catch {
      this.#replyError("Nie udało się pobrać zaktualizowanego statusu wątku.")
      return
    }
    if (this.hasThreadMetaTarget) {
      this.threadMetaTarget.textContent = `${thread.status_label} · ${thread.reason_name}`
    }
    if (this.hasThreadCloseBtnTarget) {
      this.threadCloseBtnTarget.hidden = thread.status === "closed"
    }
    await this.#loadAudit()
  }

  // --- nowy wątek ---

  async create(event) {
    event.preventDefault()
    if (this.creating) return
    const reason = comboValue(this.hasReasonSelectTarget ? this.reasonSelectTarget : null)
    if (!reason) {
      this.#formError("Wybierz powód kontaktu.")
      return
    }
    const subject = this.subjectInputTarget.value.trim()
    const body = this.bodyInputTarget.value.trim()
    if (!subject || !body) {
      this.#formError("Uzupełnij temat i treść wiadomości.")
      return
    }
    const payload = { subject, body, reason: Number(reason) }
    const orderVal = comboValue(this.hasOrderSelectTarget ? this.orderSelectTarget : null)
    if (orderVal) {
      payload.order = String(orderVal)
    }
    this.creating = true
    if (this.hasCreateBtnTarget) this.createBtnTarget.disabled = true
    const res = await sendJSON("POST", this.urlValue, payload)
    this.creating = false
    if (this.hasCreateBtnTarget) this.createBtnTarget.disabled = false
    if (!res.ok) {
      const fallback = res.status === 429
        ? "Przekroczono dzienny limit nowych wątków. Spróbuj ponownie jutro."
        : "Nie udało się utworzyć wątku."
      this.#formError(errorDetail(res.data, fallback))
      return
    }
    this.#formError(null)
    this.subjectInputTarget.value = ""
    this.bodyInputTarget.value = ""
    await Promise.all([
      clearCombo(this.application, this.hasReasonSelectTarget ? this.reasonSelectTarget : null),
      clearCombo(this.application, this.hasOrderSelectTarget ? this.orderSelectTarget : null),
    ])
    this.page = 1
    await this.loadList()
  }

  // --- prywatne ---

  #resetReply() {
    this.composerGeneration = (this.composerGeneration ?? 0) + 1
    this.replySending = false
    if (this.hasReplyBodyTarget) this.replyBodyTarget.value = ""
    if (this.hasReplyBtnTarget) this.replyBtnTarget.disabled = false
    this.#replyError(null)
  }

  #listUrl() {
    const params = new URLSearchParams()
    if (this.page > 1) params.set("page", String(this.page))
    const query = params.toString()
    return query ? `${this.urlValue}?${query}` : this.urlValue
  }

  #buildRow(template, thread) {
    const row = template.content.firstElementChild.cloneNode(true)
    const badge = row.querySelector('[data-field="status"]')
    badge.textContent = thread.status_label
    badge.className = `ls-badge ls-badge--${STATUS_BADGE[thread.status] ?? "new"}`
    badge.setAttribute("data-status", thread.status)
    row.querySelector('[data-field="reason"]').textContent = thread.reason_name
    row.querySelector('[data-field="subject"]').textContent = thread.subject
    row.querySelector('[data-field="last"]').textContent = formatDate(thread.last_message_at ?? thread.created)
    row.querySelector('[data-field="open"]').setAttribute("data-helpdesk-id-param", thread.id)
    return row
  }

  // After a mutation: the client page re-renders its rows, the staff inbox reloads its data-table (and clears an old error).
  async #refreshList() {
    if (this.hasRowsTarget) {
      await this.loadList()
      return
    }
    this.#status(null)
    const table = this.element.querySelector('[data-controller~="data-table"]')
    const ctrl = table ? this.application.getControllerForElementAndIdentifier(table, "data-table") : null
    if (ctrl) await ctrl.reload()
  }

  async #loadMessages() {
    let messages
    try {
      messages = await getAllJSON(`${this.urlValue}${this.currentThreadId}/messages/`)
    } catch {
      this.#replyError("Nie udało się załadować wiadomości.")
      return
    }
    this.messagesTarget.textContent = ""
    const template = document.getElementById("helpdesk-message-template")
    for (const message of messages) {
      const node = template.content.firstElementChild.cloneNode(true)
      node.querySelector('[data-field="author"]').textContent = message.author_name || "—"
      node.querySelector('[data-field="created"]').textContent = formatDate(message.created)
      node.querySelector('[data-field="body"]').textContent = message.body
      if (message.is_staff_reply) node.classList.add("is-staff")
      this.messagesTarget.appendChild(node)
    }
  }

  async #loadAudit() {
    if (!this.hasAuditTarget || this.currentThreadId === null) return
    let data
    try {
      data = await getJSON(`${this.urlValue}${this.currentThreadId}/audit/`)
    } catch {
      return  // silently skip — audit is supplementary; do not block the thread view
    }
    this.auditListTarget.textContent = ""
    const template = document.getElementById("helpdesk-audit-row-template")
    for (const event of (data.results ?? [])) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.querySelector('[data-field="when"]').textContent = formatDate(event.created)
      row.querySelector('[data-field="what"]').textContent = AUDIT_LABELS[event.action] ?? event.action
      row.querySelector('[data-field="who"]').textContent = event.actor
      this.auditListTarget.appendChild(row)
    }
  }

  async #loadDictionaries() {
    if (this.hasReasonSelectTarget) {
      try {
        const reasons = await getJSON(`${this.urlValue}reasons/`)
        const opts = reasons.map(r => ({ value: r.id, label: r.name }))
        await setComboOptions(this.application, this.reasonSelectTarget, opts)
      } catch { /* słownik niedostępny — combobox zostaje pusty */ }
    }
    if (this.hasOrderSelectTarget && this.ordersUrlValue) {
      try {
        const orders = await getAllJSON(this.ordersUrlValue)
        const opts = orders.map(o => ({
          value: o.code,
          label: `Zamówienie #${o.code} · ${formatDate(o.created)}`,
        }))
        await setComboOptions(this.application, this.orderSelectTarget, opts)
      } catch { /* brak zamówień do podpięcia — combobox zostaje pusty */ }
    }
  }

  #updatePager(data) {
    this.hasNext = Boolean(data.next)
    if (this.hasPagerTarget) this.pagerTarget.hidden = this.count === 0
    if (this.hasPageInfoTarget) this.pageInfoTarget.textContent = `strona ${this.page} (${this.count})`
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = this.page <= 1
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !this.hasNext
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return
    this.formErrorTarget.hidden = message === null
    if (message !== null) this.formErrorTarget.textContent = message
  }

  #replyError(message) {
    if (!this.hasReplyErrorTarget) return
    this.replyErrorTarget.hidden = message === null
    this.replyErrorTarget.textContent = message ?? ""
  }

  #status(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = message === null
    if (message !== null) this.statusTarget.textContent = message
  }
}
